import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { AsYouType, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';

import {
  DEFAULT_PHONE_COUNTRY,
  normalizePhoneToE164,
  PHONE_COUNTRY_OPTIONS,
} from '../lib/phoneIntl';

function parseValueToLocal(value: string): { country: CountryCode; display: string } {
  const trimmed = value.trim();
  if (!trimmed) {
    return { country: DEFAULT_PHONE_COUNTRY, display: '' };
  }
  const direct = parsePhoneNumberFromString(trimmed);
  if (direct?.country) {
    return { country: direct.country, display: direct.formatNational() };
  }
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) {
    return { country: DEFAULT_PHONE_COUNTRY, display: '' };
  }
  const withDefault = parsePhoneNumberFromString(digits, DEFAULT_PHONE_COUNTRY);
  if (withDefault?.nationalNumber) {
    const ty = new AsYouType(DEFAULT_PHONE_COUNTRY);
    return { country: DEFAULT_PHONE_COUNTRY, display: ty.input(withDefault.nationalNumber) };
  }
  const ty = new AsYouType(DEFAULT_PHONE_COUNTRY);
  return { country: DEFAULT_PHONE_COUNTRY, display: ty.input(digits) };
}

export type PhoneFieldProps = {
  id?: string;
  value: string;
  onChange: (e164: string) => void;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  selectClassName?: string;
  inputClassName?: string;
  placeholder?: string;
  autoComplete?: string;
};

/**
 * Country calling-code select + national number input. Emits E.164 (or +country+digits) via `onChange`.
 */
export function PhoneField({
  id,
  value,
  onChange,
  disabled,
  required,
  className = '',
  selectClassName = '',
  inputClassName = '',
  placeholder = 'Phone number',
  autoComplete = 'tel',
}: PhoneFieldProps) {
  const lastEmitted = useRef<string | null>(null);
  const [country, setCountry] = useState<CountryCode>(() => parseValueToLocal(value).country);
  const [display, setDisplay] = useState(() => parseValueToLocal(value).display);

  useEffect(() => {
    if (value === (lastEmitted.current ?? '')) return;
    const next = parseValueToLocal(value);
    setCountry(next.country);
    setDisplay(next.display);
    lastEmitted.current = value;
  }, [value]);

  const emit = (c: CountryCode, disp: string) => {
    const e164 = normalizePhoneToE164(c, disp) ?? '';
    lastEmitted.current = e164;
    onChange(e164);
  };

  const onCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const c = e.target.value as CountryCode;
    const digits = display.replace(/\D/g, '');
    const nextDisp = digits ? new AsYouType(c).input(digits) : '';
    setCountry(c);
    setDisplay(nextDisp);
    emit(c, nextDisp);
  };

  const onNationalChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formatted = new AsYouType(country).input(e.target.value);
    setDisplay(formatted);
    emit(country, formatted);
  };

  return (
    <div className={`flex flex-wrap items-stretch gap-2 ${className}`}>
      <select
        id={id ? `${id}-country` : undefined}
        aria-label="Country calling code"
        className={
          selectClassName.trim()
            ? selectClassName
            : `min-w-[6.5rem] shrink-0 rounded-lg border border-white/15 bg-white/[0.92] px-2 py-2 font-body text-xs font-medium text-neutral-800 shadow-inner shadow-black/5 outline-none focus:border-[var(--home-miami-blue)] focus:ring-2 focus:ring-[var(--home-miami-blue)]/30 disabled:opacity-60`
        }
        value={country}
        disabled={disabled}
        onChange={onCountryChange}
      >
        {PHONE_COUNTRY_OPTIONS.map((o) => (
          <option key={o.code} value={o.code}>
            {o.label}
          </option>
        ))}
      </select>
      <input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete={autoComplete}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={
          inputClassName.trim()
            ? inputClassName
            : `min-w-0 flex-1 rounded-lg border border-white/15 bg-white/[0.92] px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 shadow-inner shadow-black/5 outline-none focus:border-[var(--home-miami-blue)] focus:ring-2 focus:ring-[var(--home-miami-blue)]/30 disabled:opacity-60`
        }
        value={display}
        onChange={onNationalChange}
      />
    </div>
  );
}
